import React, { useEffect, useState } from "react";
import PageTitle from "../../components/page-title/pageTitle";
import { BiSolidUser } from "react-icons/bi";
import { useNavigate, useLocation } from "react-router-dom";
import { post } from "../../utils/fetchAPI";
import { Bounce, toast } from "react-toastify";

const DoctorCreatePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const editingUser = location.state?.user || null;
    const [users, setUsers] = useState([]);
    const [changedData, setChangedData] = useState({});

    const [userData, setUserData] = useState({
        name: "",
        email: "",
        phone: "",
        password: "",
        dob: "",
        gender: "",
        address: "",
        type: "doctor", // DEFAULT DOCTOR
        status: "",
        details: {
            perDayLimit: "",
            bmdcRegNo: "",
            smsBalance: "",
        },
    });


    useEffect(() => {
        // fetchUsers();

        if (editingUser) {
            setUserData({
                ...editingUser,
                dob: editingUser.dob
                    ? new Date(editingUser.dob).toISOString().split("T")[0]
                    : "",
                type: "doctor", // force doctor
            });
        }
    }, []);

    // Handle form input
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (["perDayLimit", "smsBalance", "bmdcRegNo"].includes(name)) {
            setUserData((prev) => ({
                ...prev,
                details: { ...prev.details, [name]: value },
            }));
        } else {
            setUserData((prev) => ({
                ...prev,
                [name]: value
            }));
        }

        if (["perDayLimit", "smsBalance", "bmdcRegNo"].includes(name)) {
            setChangedData((prev) => ({
                ...prev,
                details: {
                    ...prev.details,
                    [name]: value,
                },
            }));
        } else {
            setChangedData((prev) => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Submit Form
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingUser) {
                // UPDATE USER
                const updateData = { ...changedData };
                delete changedData.password;
                await post(`user/update/${editingUser._id}`, updateData);
                toast.success("Doctor updated successfully!", {
                    position: "top-center",
                    transition: Bounce,
                });
            } else {
                // CREATE USER
                await post("user/create", userData);
                toast.success("Doctor created successfully!", {
                    position: "top-center",
                    transition: Bounce,
                });
            }

            // await fetchUsers();
            navigate("/doctor-list");
        } catch (error) {
            if (error.response?.status === 409) {
                toast.error("This email is already used!", {
                    position: "top-center",
                });
            } else {
                toast.error("Failed to save doctor info.", {
                    position: "top-center",
                });
            }
        }
    };

    return (
        <div className="w-full p-5">
            <div className="flex justify-between items-center mb-5">
                <PageTitle
                    icon={<BiSolidUser />}
                    title={editingUser ? "Edit Doctor" : "Create Doctor"}
                />

                <button
                    onClick={() => navigate("/doctor-list")}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md text-sm"
                >
                    Back
                </button>
            </div>

            {/* FORM */}
            <div className="bg-white shadow-md rounded-md p-6">
                <h2 className="text-blue-500 text-2xl font-bold mb-4">
                    {editingUser ? "Edit Doctor Info" : "Create New Doctor"}
                </h2>

                <form
                    className="text-black text-sm flex flex-col w-full max-h-[35rem] overflow-y-auto"
                    onSubmit={handleSubmit}
                >
                    {/* Name + Email */}
                    <div className="flex flex-col lg:flex-row gap-5 mb-3">
                        <div className="w-full">
                            <label>Name *</label>
                            <input
                                name="name"
                                value={userData?.name}
                                placeholder="Doctor Name"
                                onChange={handleInputChange}
                                className="border p-2 rounded-md w-full"
                                required
                            />
                        </div>

                        <div className="w-full">
                            <label>Email *</label>
                            <input
                                name="email"
                                value={userData?.email}
                                placeholder="Email"
                                onChange={handleInputChange}
                                className="border p-2 rounded-md w-full"
                                required
                            />
                        </div>
                    </div>

                    {/* Password */}
                    {!editingUser && (
                        <div className="flex flex-col lg:flex-row gap-5 mb-3">
                            <div className="w-full">
                                <label>Password *</label>
                                <input
                                    type="password"
                                    name="password"
                                    value={userData?.password}
                                    placeholder="Password"
                                    onChange={handleInputChange}
                                    className="border p-2 rounded-md w-full"
                                    required
                                />
                            </div>
                        </div>
                    )}

                    {/* Address + Status */}
                    <div className="flex flex-col lg:flex-row gap-5 mb-3">
                        <div className="w-full">
                            <label>Address *</label>
                            <input
                                name="address"
                                value={userData?.address}
                                placeholder="Address"
                                onChange={handleInputChange}
                                className="border p-2 rounded-md w-full"
                                required
                            />
                        </div>

                        <div className="w-full">
                            <label>Status *</label>
                            <select
                                name="status"
                                value={userData?.status}
                                onChange={handleInputChange}
                                className="border p-2 rounded-md w-full"
                                required
                            >
                                <option value="">Select Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>

                    {/* Doctor Type Auto-Fixed */}
                    <div className="w-full mb-3">
                        <label>Type *</label>
                        <input
                            value="doctor"
                            readOnly
                            className="border p-2 rounded-md bg-gray-100 cursor-not-allowed w-full"
                        />
                    </div>

                    {/* DOB + Gender */}
                    <div className="flex flex-col lg:flex-row gap-5 mb-3">
                        <div className="w-full">
                            <label>Date of Birth *</label>
                            <input
                                type="date"
                                name="dob"
                                value={userData?.dob}
                                onChange={handleInputChange}
                                className="border p-2 rounded-md w-full"
                                required
                            />
                        </div>

                        <div className="w-full">
                            <label>Gender *</label>
                            <select
                                name="gender"
                                value={userData?.gender}
                                onChange={handleInputChange}
                                className="border p-2 rounded-md w-full"
                                required
                            >
                                <option value="">Select Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                            </select>
                        </div>
                    </div>

                    {/* Details */}
                    <div className="flex flex-col lg:flex-row gap-5 mb-3">
                        <div className="w-full">
                            <label>Per Day Limit *</label>
                            <input
                                name="perDayLimit"
                                value={userData?.details?.perDayLimit}
                                placeholder="Per Day Limit"
                                onChange={handleInputChange}
                                className="border p-2 rounded-md w-full"
                                required
                            />
                        </div>

                        <div className="w-full">
                            <label>BMDC Reg No *</label>
                            <input
                                name="bmdcRegNo"
                                value={userData?.details?.bmdcRegNo}
                                placeholder="BMDC Registration No"
                                onChange={handleInputChange}
                                className="border p-2 rounded-md w-full"
                                required
                            />
                        </div>

                        <div className="w-full">
                            <label>SMS Balance *</label>
                            <input
                                type="number"
                                name="smsBalance"
                                value={userData?.details?.smsBalance}
                                placeholder="SMS Balance"
                                onChange={handleInputChange}
                                className="border p-2 rounded-md w-full"
                                required
                            />
                        </div>
                    </div>

                    {/* Phone */}
                    <div className="mb-3">
                        <label>Phone</label>
                        <input
                            name="phone"
                            type="number"
                            value={userData?.phone}
                            placeholder="Phone Number"
                            onChange={handleInputChange}
                            className="border p-2 rounded-md w-full"
                        />
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-4 mt-6">
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-500 text-white rounded-md"
                        >
                            {editingUser ? "Update" : "Create"}
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate("/doctor-list")}
                            className="px-4 py-2 bg-gray-300 rounded-md"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DoctorCreatePage;
